/**
 * Janus lane MV1 · M4a — Squeezed / Very Squeezed at a move's resting place, the TS twin of
 * ft_apply_squeeze_to_creature_at_rest() (includes/functions/janus/encounter/apply_squeezed_at_move_end.php) for a
 * PROMOTED (shaped) encounter. Oracle: tools/test/fixtures/mv1-squeeze-oracle.json (the real PHP function captured on the
 * MV1 cell by tools/probes/mv1_cell_fixture.php capture-squeeze) → tools/test/janus-mv1-squeeze.test.mjs.
 *
 * The shaped rule reads physical headroom from the endpoint's feet on its band. Open sky supplies no cap; a ceiling contact
 * is on the deck above. A supported stair uses its open route corridor. Height = the size-tier cube, compared at the nearest 0.5 ft.
 *   Squeezed       height >  room height
 *   Very Squeezed  height >= 2 × room height   (stacks on Squeezed)
 * Mesh bodies and preferred burrowing keep their existing exemption. Horizontal fit is measured by the movement
 * reader on the endpoint's floor band and mid-body height, with live openings and the shared SizeType ladder.
 */
import { stairGeometry, type ShapeBox } from '../inventory/floor-move.js';
import { boxIsNavigationOpenGround } from '../../../../ft3d/engine/ft3d-shape-core.js';
export { boxIsNavigationOpenGround } from '../../../../ft3d/engine/ft3d-shape-core.js';
export { SIZE_TIER_FT, squeezeBodyFt } from '../../../../ft3d/engine/ft3d-creature-size.js';
type Row = Record<string, unknown>;
const EPS = 1e-6; // FT_SHAPE_EPS
const BAND_CONTACT_FT = 0.01; // Continuous-world floor/ceiling contact tolerance.
const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
export const SQUEEZED_ITEM_ID = '75682F12-AB67-401B-97D7-F1B7354333CA';
export const VERY_SQUEEZED_ITEM_ID = 'B45813FE-321A-EC7F-F008-12B8FD33B524';
export const SPEED_BURROWING_TYPE_ID = 'ACFB413D-15A8-835B-C821-9E101B6C4F60';

/** A real stair tread/apron opens its source ceiling exactly as prepareStairWalkRoute does.
 * The held boxes stay intact, including the lower ceiling beneath or beside the stair. */
function stairCeilingsAt(shape: Row | null, boxes: Row[], x: number, y: number, z: number): Map<Row, number> {
  const ceilings = new Map<Row, number>();
  const connections = record(shape?.authoring)?.verticalConnections;
  for (const value of Array.isArray(connections) ? connections : []) {
    const connection = record(value);
    if (!connection) continue;
    let stair: ReturnType<typeof stairGeometry>;
    try { stair = stairGeometry(connection, boxes as unknown as ShapeBox[]); } catch { continue; }
    if (!(stair.width > 0) || stair.points.some(point => !point.every(Number.isFinite))) continue;
    const source = boxes.find(box => box.id === connection.sourceRoomId), target = boxes.find(box => box.id === connection.targetRoomId);
    if (!source || !target || !(Number(target.floor) > Number(source.floor))) continue;
    for (let i = 1; i < stair.points.length; i++) {
      const a = stair.points[i - 1]!, b = stair.points[i]!;
      const dx = b[0] - a[0], dy = b[1] - a[1], length2 = dx * dx + dy * dy;
      if (!(length2 > 0)) continue;
      const t = ((x - a[0]) * dx + (y - a[1]) * dy) / length2;
      // Match stairSqueezeLevelAt's persisted-coordinate and lateral tread tolerances.
      if (t < -.002 / Math.sqrt(length2) || t > 1 + .002 / Math.sqrt(length2)
        || Math.hypot(x - a[0] - t * dx, y - a[1] - t * dy) > stair.width / 2 + .002
        || Math.abs(z - a[2] - t * (b[2] - a[2])) > .002) continue;
      ceilings.set(source, Math.max(ceilings.get(source) ?? -Infinity, Number(target.ceiling)));
      break;
    }
  }
  return ceilings;
}

/** Headroom on the endpoint's physical band. A ceiling-only contact stands on
 * the deck, rather than in the room below. Omitted z retains the old 2D oracle. */
export function squeezeRoomHeight(raw: unknown, x: number, y: number, z?: number): number | null {
  const shape = record(raw);
  const boxes = (Array.isArray(shape?.boxes) ? shape.boxes as unknown[] : []).map(record).filter((b): b is Row => b !== null);
  const stairCeilings = z === undefined ? new Map<Row, number>() : stairCeilingsAt(shape, boxes, x, y, z);
  const ceilingOf = (box: Row): number => stairCeilings.get(box) ?? Number(box.ceiling);
  const containing = boxes.filter(box => {
    const bx = record(box.x) ?? {}, by = record(box.y) ?? {};
    return x >= Number(bx.min) - EPS && x <= Number(bx.max) + EPS && y >= Number(by.min) - EPS && y <= Number(by.max) + EPS
      && (z === undefined || z >= Number(box.floor) - BAND_CONTACT_FT && z < ceilingOf(box) - BAND_CONTACT_FT);
  });
  if (!containing.length) return null;
  const structural = containing.filter(box => !boxIsNavigationOpenGround(box));
  const active = structural.length ? structural : containing;
  // An open room's authored navigation volume is not a roof. Keep the legacy metadata-free
  // box contract; explicit enclosure/roof state follows ft3dFlightStepCrossesRoof.
  const capped = z === undefined ? active : active.filter(box => {
    const creation = record(box.creation) ?? {};
    return !boxIsNavigationOpenGround(box) && creation.roofRemoved !== true
      && (creation.enclosure === undefined || String(creation.enclosure).includes('ceiling'));
  });
  if (!capped.length) return null;
  const floor = Math.min(...active.map(box => Number(box.floor)));
  // Metadata-free legacy boxes keep MAIN's full room height. Only an authored
  // ceiling establishes endpoint-relative headroom for a hovering/supported body.
  return Math.max(0, ...capped.map(box => ceilingOf(box)
    - (z === undefined || record(box.creation)?.enclosure === undefined ? floor : Math.max(floor, z))));
}

export interface SqueezeState { squeezed: boolean; very: boolean }
export function squeezeAtRest(input: { shape: unknown; x: number; y: number; z?: number; heightFt: number; meshBody: boolean; preferredSpeedTypeId: string;
  horizontalLevel?: 0 | 1 | 2 | null; geometryExempt?: boolean }): SqueezeState {
  const room = squeezeRoomHeight(input.shape, input.x, input.y, input.z);
  if (input.meshBody || input.geometryExempt || !(input.heightFt > 0) || input.preferredSpeedTypeId.trim().toUpperCase() === SPEED_BURROWING_TYPE_ID) {
    return { squeezed: false, very: false };
  }
  const compare = Math.round(input.heightFt * 2) / 2;
  // A GM may deliberately drop a standee below the passage floor. It remains placed and Very Squeezed.
  const horizontal = input.horizontalLevel === null ? 2 : input.horizontalLevel ?? 0;
  return { squeezed: (room !== null && compare > room) || horizontal > 0,
    very: (room !== null && compare >= 2 * room) || horizontal === 2 };
}

/** A resolved status row's catalog item id: the lean top-level `status_item_id`, or the embedded definition's
 *  `status_arr.items.item_id` (the shape a compiled encounter row carries — there is no top-level id there). */
export function statusItemIdOf(status: Row): string {
  const embedded = record(record(status.status_arr)?.items);
  const raw = status.status_item_id ?? embedded?.item_id ?? status.item_id ?? '';
  return String(Array.isArray(raw) ? raw[0] : raw).trim().toUpperCase();
}

/** The status delta a move owes: which of the two canonical statuses to add, and which held rows to remove. */
export function squeezeDelta(state: SqueezeState, held: { squeezed: string[]; very: string[] }):
  { add: string[]; remove: string[] } {
  const add: string[] = [], remove: string[] = [];
  if (state.squeezed && !held.squeezed.length) add.push(SQUEEZED_ITEM_ID);
  if (!state.squeezed) remove.push(...held.squeezed);
  if (state.very && !held.very.length) add.push(VERY_SQUEEZED_ITEM_ID);
  if (!state.very) remove.push(...held.very);
  return { add, remove };
}
